import { humanoidFace, humanoidSkull } from './humanoid-head.js';
import type { HumanFace, HumanRigId, HumanSkull } from './human.js';
import { NOSE_SHAPE_DEFAULT, type NoseShape } from './humanoid-nose.js';

/** Elf facial proportions in the shared skull coordinate space. */
export interface ElfFaceMorph extends NoseShape {
  jawWidth: number; cheekWidth: number; cheek: number; hollow: number; brow: number;
  noseWidth: number; noseLength: number; noseProjection: number; noseRootHeight: number; noseBridge: number;
  mouthWidth: number; lipFullness: number; eyeSize: number; eyeDepth: number;
  /** Forward offset of the eye-level cross-section from the matching Human skull. */
  eyeLevelCentreOffset: number;
  /** Orbital plane before the Eye depth inset; the morph control shows the resulting skull plane. */
  eyePlane: number; eyeOpening: number; eyeTilt: number;
  earLength: number; earOutward: number; earBack: number;
}
export const ELF_FACE_DEFAULT: ElfFaceMorph = {
  ...NOSE_SHAPE_DEFAULT, bridgeWidth: .85, wingWidth: .85,
  jawWidth: 1, cheekWidth: 1, cheek: .018, hollow: .008, brow: .003,
  noseWidth: .82, noseLength: .038, noseProjection: .094, noseRootHeight: 1.865, noseBridge: 0,
  mouthWidth: .023, lipFullness: 1, eyeSize: 1.12, eyeLevelCentreOffset: 0, eyePlane: .08075, eyeDepth: .0055, eyeOpening: .95, eyeTilt: .09,
  earLength: .07, earOutward: .036, earBack: .024,
};
export const elfFaces: { id: string; name: string; morph?: Partial<ElfFaceMorph> }[] = [
  { id: 'standard', name: 'Elf classic' },
  { id: 'angular', name: 'High cheekbones', morph: { cheekWidth: 1.08, cheek: .023, jawWidth: .94, hollow: .011 } },
  { id: 'soft', name: 'Soft features', morph: { jawWidth: 1.04, hollow: .003, lipFullness: 1.12, eyeOpening: 1.02 } },
  { id: 'aquiline', name: 'Aquiline', morph: { noseLength: .043, noseProjection: .1, noseBridge: .003 } },
  { id: 'gaunt', name: 'Lean features', morph: { jawWidth: .92, cheekWidth: .97, hollow: .014, eyeDepth: .008 } },
  { id: 'youthful', name: 'Youthful', morph: { noseLength: .034, eyeSize: 1.18, eyeOpening: 1.02, lipFullness: 1.08, hollow: .004 } },
];
export function resolveElfFace(base: ElfFaceMorph, id = 'standard'): ElfFaceMorph {
  return { ...base, ...elfFaces.find(face => face.id === id)?.morph };
}
export function elfSkull(m: ElfFaceMorph, body: HumanRigId): HumanSkull {
  const skull = humanoidSkull(body, 0.9 * m.jawWidth, 0.96 * m.cheekWidth);
  skull.jaw.centre[5] += m.eyeLevelCentreOffset;
  return {
    ...skull,
    eye: { x: .032, y: 1.859, plane: m.eyePlane - m.eyeDepth * .5, opening: m.eyeOpening },
    ear: { ...skull.ear, length: .028, width: .016, thickness: .006, pointed: true,
      pointLength: m.earLength, pointOutward: m.earOutward, pointBack: m.earBack },
    mouth: { y: 1.794, width: m.mouthWidth, upper: 1, lower: 1, forward: 1, bow: 1.1, corners: .001, groove: .0005, overlap: .0008, lift: .0004 },
    nose: { rootY: m.noseRootHeight, length: m.noseLength, tipZ: m.noseProjection,
      ...Object.fromEntries(Object.keys(NOSE_SHAPE_DEFAULT).map(key => [key, m[key as keyof NoseShape]])) },
  };
}
export function elfFaceShape(m: ElfFaceMorph, body: HumanRigId = 'human-male'): HumanFace {
  return { ...humanoidFace(body, { jaw: .94, chin: .86, square: .75 }, { chinDepth: 0 }), brow: m.brow, cheek: m.cheek, hollow: m.hollow, eye: m.eyeDepth, eyeSize: m.eyeSize,
    nose: m.noseWidth, lips: m.lipFullness, noseLength: 0, bridge: m.noseBridge, fullness: .001,
    eyeTilt: m.eyeTilt, fold: 0 };
}
